import { useQuery } from "@tanstack/react-query";
import { Inbox, RotateCw } from "lucide-react";
import { Link } from "react-router";
import { useAuth } from "../../auth/AuthProvider";
import { Button } from "../../components/Button";
import { PageHeader, useDocumentTitle } from "../../components/layout";
import { LoadingBlock } from "../../components/Spinner";
import { Badge, EmptyState, ErrorState } from "../../components/states";
import { api } from "../../lib/api";
import type { OutboxMessage } from "../../lib/types";
import { NotFoundPage } from "../errors";

const KIND_LABEL: Record<OutboxMessage["kind"], string> = { verifyEmail: "Verify email", resetPassword: "Reset password", other: "Other" };

/** Development-only local outbox: how testers "receive" verification and reset emails. */
export function DevOutboxPage() {
  useDocumentTitle("Local outbox");
  const { config } = useAuth();
  const outbox = useQuery({
    queryKey: ["dev-outbox"],
    queryFn: ({ signal }) => api<{ messages: OutboxMessage[] }>("/api/dev/outbox", { signal, noCache: true }),
    enabled: config.devOutbox,
    refetchInterval: 4000,
  });
  if (!config.devOutbox) return <NotFoundPage />;
  return (
    <main className="page page--narrow" id="main">
      <PageHeader
        eyebrow="Development only"
        title="Local outbox"
        description="Emails the Auth emulator would have sent. Open a link in this browser to verify an address or reset a password."
        back={{ to: "/login", label: "Sign in" }}
        actions={
          <Button emphasis="outline" intent="neutral" icon={<RotateCw />} busy={outbox.isFetching && !outbox.isPending} onClick={() => void outbox.refetch()}>
            Refresh
          </Button>
        }
      />
      {outbox.isPending ? (
        <LoadingBlock label="Loading messages" />
      ) : outbox.isError ? (
        <ErrorState error={outbox.error} onRetry={() => void outbox.refetch()} />
      ) : !outbox.data.messages.length ? (
        <EmptyState icon={<Inbox />} title="No emails yet">
          Create an account or request a password reset and the message will appear here.
        </EmptyState>
      ) : (
        <ul className="outbox">
          {outbox.data.messages.map((m) => (
            <li key={m.id} className="outbox__item">
              <div className="outbox__meta">
                <Badge tone={m.kind === "verifyEmail" ? "info" : m.kind === "resetPassword" ? "warning" : "neutral"}>{KIND_LABEL[m.kind]}</Badge>
                <span className="muted">{new Date(m.receivedAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
              <p className="outbox__to">
                To <strong>{m.email}</strong>
              </p>
              <Link className="btn btn--solid btn--brand btn--sm outbox__open" to={m.link}>
                <span className="btn__content">
                  <span className="btn__label">{m.kind === "verifyEmail" ? "Verify this email" : m.kind === "resetPassword" ? "Choose a new password" : "Open link"}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
